import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { getAgentRole, canViewHealth, isReadOnly, getAgentName } from '../lib/roles';

const navItems = [
  { path: '/', label: 'Dashboard' },
  { path: '/search', label: 'Search' },
  { path: '/top-requests', label: 'Top Requests' },
  { path: '/feed', label: 'Feed' },
  { path: '/activity', label: 'Activity' },
];

// Health/status page is restricted to leadership + admins.


function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

// Sun icon for light mode (click to go dark)
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

// Moon icon for dark mode (click to go light)
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

// Hamburger icon
const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// Close icon
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function Header({ onLogout }) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const items = canViewHealth()
    ? [...navItems, { path: '/health', label: 'Health' }]
    : navItems;

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('feedback_token');
    localStorage.removeItem('feedback_agent');
    if (onLogout) onLogout();
    window.location.href = '/login';
  };

  const readOnlyChip = isReadOnly() ? (
    <span
      title="Read-only access: you can view and export everything, but not change cluster data."
      style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
        textTransform: 'uppercase', color: 'var(--text-muted)',
        background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
        borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap',
      }}
    >
      Read only
    </span>
  ) : null;

  const themeBtn = (
    <button
      onClick={toggleTheme}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 8,
        background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
        color: 'var(--text-muted)', cursor: 'pointer',
        transition: 'background 0.15s, color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  );

  return (
    <header style={{
      background: 'var(--bg-primary)',
      borderBottom: '1px solid var(--border-color)',
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 52,
      }}>
        {/* Brand */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 11, textDecoration: 'none' }}>
          <img
            src="/coros-hex.svg"
            alt="COROS"
            width={26}
            height={29}
            style={{ display: 'block' }}
          />
          <div>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 17, fontWeight: 900,
              textTransform: 'uppercase',
              color: 'var(--text-primary)',
              lineHeight: 1, letterSpacing: '0.01em',
            }}>
              Feedback Intelligence
            </div>
            {!isMobile && (
              <div style={{
                fontFamily: 'var(--font-display)',
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.14em',
                color: 'var(--coros-red)', lineHeight: 1, marginTop: 3,
              }}>
                RELAY · COROS
              </div>
            )}
          </div>
        </Link>

        {/* Desktop nav */}
        {!isMobile && (
          <nav style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    padding: '6px 14px', borderRadius: 6,
                    fontSize: 13, fontWeight: 600, textDecoration: 'none',
                    transition: 'background 0.15s, color 0.15s',
                    background: isActive ? 'var(--bg-tertiary)' : 'transparent',
                    color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; } }}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {readOnlyChip}{themeBtn}
          {!isMobile && (
            <>
              <button
                onClick={handleLogout}
                style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text-muted)', background: 'transparent',
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'color 0.15s', padding: '4px 8px', borderRadius: 4,
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--color-error)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
              <div style={{ width: 1, height: 20, background: 'var(--border-color)', margin: '0 4px' }} />
              <a
                href="https://relay.infinitusdigitalllc.com"
                style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text-muted)', textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                </svg>
                Back to RELAY
              </a>
            </>
          )}
          {isMobile && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8,
                background: menuOpen ? 'var(--bg-hover)' : 'transparent',
                border: 'none', color: 'var(--text-primary)', cursor: 'pointer',
              }}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <div style={{
          borderTop: '1px solid var(--border-color)',
          background: 'var(--bg-primary)',
          padding: '8px 16px 12px',
        }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    padding: '10px 12px', borderRadius: 8,
                    fontSize: 14, fontWeight: 600, textDecoration: 'none',
                    background: isActive ? 'var(--bg-tertiary)' : 'transparent',
                    color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 8, paddingTop: 8 }}>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 12px', borderRadius: 8,
                fontSize: 13, fontWeight: 600,
                color: 'var(--color-error)', background: 'transparent',
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                width: '100%', textAlign: 'left',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
            <a
              href="https://relay.infinitusdigitalllc.com"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 12px', borderRadius: 8,
                fontSize: 13, fontWeight: 600,
                color: 'var(--text-muted)', textDecoration: 'none',
                marginTop: 4,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
              </svg>
              Back to RELAY
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
